import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Refs, Row } from "../types";
import { api, ApiError, type Observacao } from "../api";
import ErrorState from "./ErrorState";
import FreelancersModal from "./FreelancersModal";
import { IconClose, IconTrash, IconUsers } from "../icons";
import { ordenarPorNome } from "../ordem";
import {
  AGENDA_CORES,
  COR_LIVRE_PADRAO,
  corLivre,
  corPeloTexto,
  palavraDaCor,
  pintar,
} from "../agenda-cores";

interface Props {
  refs: Refs;
  onDataChanged: () => void;
}

/**
 * UMA LINHA DA GRADE — e ela tem duas origens.
 *
 * `colaborador` vem do espelho do RH/DP (a equipe do quadro). `freelancer` vem
 * de uma tabela que só existe aqui, para pôr na escala quem é contratado por
 * fora sem virar ficha de RH. As duas ocupam a MESMA célula da agenda, em
 * colunas diferentes — ver server/agenda-freelancers.ts.
 */
interface LinhaAgenda {
  tipo: "colaborador" | "freelancer";
  id: number;
  nome: string;
}

/**
 * A chave da célula no mapa da grade.
 *
 * O prefixo NÃO é enfeite: colaborador 7 e freelancer 7 são pessoas
 * diferentes, e sem ele os dias de um apareceriam na linha do outro.
 */
const chaveCelula = (l: Pick<LinhaAgenda, "tipo" | "id">, dia: string) =>
  `${l.tipo === "freelancer" ? "f" : "c"}${l.id}|${dia}`;

/** Como o servidor quer receber uma pessoa da grade: um dos dois ids, nunca os dois. */
const comoDono = (l: LinhaAgenda) =>
  l.tipo === "freelancer" ? { freelancer_id: l.id } : { colaborador_id: l.id };

type RespostaLote = Awaited<ReturnType<typeof api.agendaPeriodo>>;

/** "2026-10-08" -> "08/10" (as listas de dias pulados). */
const ddmm = (s: string) => `${s.slice(8)}/${s.slice(5, 7)}`;

/**
 * O que aconteceu com cada PARTICIPANTE, numa frase para a barra de resultado.
 *
 * Diz o nome de quem recebeu e — o que importa — quem ficou de fora e por quê.
 * Um participante com folga aprovada naquele dia não é marcado, e sem esta
 * frase o planejador acharia que a equipe inteira está escalada.
 */
function resumoParticipantes(r: RespostaLote, emPeriodo: boolean): string {
  const outros = (r.pessoas ?? []).filter((p) => !p.principal);
  if (outros.length === 0) return "";
  const itens = outros.map((p) => {
    const fora = p.pulados.length
      ? ` — não marcado em ${p.pulados.map((x) => `${ddmm(x.data)} (${x.motivo})`).join("; ")}`
      : "";
    const quantos = emPeriodo && p.dias.length > 0 ? ` (${p.dias.length} dia(s))` : "";
    return `${p.nome}${quantos}${fora}`;
  });
  return `Participantes: ${itens.join(" · ")}.`;
}

// A paleta (e a regra da cor livre) mora em src/agenda-cores.ts, porque o
// SERVIDOR valida com ela o que chega no campo `cor` — uma regra só para os
// dois lados. Reexportada aqui só para não quebrar quem já importava daqui.
export { AGENDA_CORES } from "../agenda-cores";

/* ----------------------------- Helpers de data ----------------------------- */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
/** "2026-09-03" -> Date local (sem o susto de fuso do `new Date(string)`). */
function parseYmd(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}
/**
 * O 409 do período não é erro de sistema: é o servidor perguntando o que fazer
 * com os dias que já têm serviço. Vira exceção só para subir até o modal, que
 * mostra a lista e oferece substituir ou manter.
 */
export class ConflitoPeriodo extends Error {
  dados: any;
  constructor(dados: any) {
    super("conflito");
    this.dados = dados;
  }
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
/** Segunda-feira da semana da data informada. */
function startOfWeekMonday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7; // segunda = 0
  x.setDate(x.getDate() - dow);
  return x;
}

const fmtWeekday = new Intl.DateTimeFormat("pt-BR", { weekday: "long" });
const fmtDay = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
const fmtRange = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" });

export default function AgendaView({ refs, onDataChanged }: Props) {
  const [start, setStart] = useState<Date>(() => startOfWeekMonday(new Date()));
  const [span, setSpan] = useState<number>(7);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [cell, setCell] = useState<{ linha: LinhaAgenda; date: string; entry: Row | null } | null>(
    null,
  );
  /**
   * Os freelancers da agenda. Carregados AQUI e não nas `refs` do App de
   * propósito: eles existem só nesta aba — nenhuma outra tela do sistema
   * (Equipe, Atividades, indicadores) deve enxergá-los como equipe.
   */
  const [freelancers, setFreelancers] = useState<Row[]>([]);
  const [gerindoFreelancers, setGerindoFreelancers] = useState(false);
  /** O resultado da última marcação em lote (fica na barra, acima da grade). */
  const [resultadoLote, setResultadoLote] = useState<string | null>(null);
  /**
   * Os dias que ACABARAM de ser marcados, para a grade contorná-los.
   *
   * Sem isto, marcar 15 dias recarrega a tabela e a pessoa não sabe o que
   * mudou — ainda mais quando o período começa fora da semana que está na tela.
   */
  const [destaque, setDestaque] = useState<Set<string>>(new Set());
  /**
   * Alguma observação foi criada/apagada com o modal aberto?
   *
   * As observações são gravadas na hora (é uma conversa, não um formulário), e
   * quem mostra o número vermelho é a GRADE. Sem esta marca, fechar o modal no
   * "Cancelar" deixaria a bolinha com o número velho até alguém recarregar a
   * página. Um `ref` porque isto não desenha nada — só decide se recarrega ao
   * fechar; num `useState` cada tecla de observação repintaria a grade inteira.
   */
  const obsMudou = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [agenda, freelas] = await Promise.all([api.list("agenda"), api.list("freelancers")]);
      setRows(agenda);
      setFreelancers(freelas);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Fecha o modal e, se houve recado novo, recarrega para o número bater. */
  const fecharCelula = useCallback(() => {
    setCell(null);
    if (obsMudou.current) {
      obsMudou.current = false;
      void load();
    }
  }, [load]);

  const days = useMemo(
    () => Array.from({ length: span }, (_, i) => addDays(start, i)),
    [start, span],
  );

  const map = useMemo(() => {
    const m = new Map<string, Row>();
    for (const r of rows) {
      const d = String(r.data).slice(0, 10);
      // A célula é de um freelancer quando `freelancer_id` está preenchido —
      // as duas colunas nunca vêm juntas (o servidor recusa).
      m.set(r.freelancer_id != null ? `f${r.freelancer_id}|${d}` : `c${r.colaborador_id}|${d}`, r);
    }
    return m;
  }, [rows]);

  const today = ymd(new Date());
  // Linhas da grade em ordem alfabética CRESCENTE (A → Z). O servidor já manda
  // ordenado; a ordenação aqui é a garantia da TELA — é nela que a ordem foi
  // pedida, e ela não pode depender de quem chamou a API antes. A regra (pt-BR,
  // acento e maiúscula tratados — "Álvaro" perto de "Adolfo", não no fim) mora
  // em src/ordem.ts, a mesma usada em todas as listas de nomes do sistema.
  const linhas = useMemo(() => {
    const equipe: LinhaAgenda[] = refs.colaboradores.map((c) => ({
      tipo: "colaborador",
      id: Number(c.id),
      nome: String(c.nome ?? ""),
    }));
    // Só os ATIVOS entram na grade: inativar é como se tira um freelancer da
    // escala sem apagar os dias que ele já trabalhou.
    const extras: LinhaAgenda[] = freelancers
      .filter((f) => f.ativo !== false)
      .map((f) => ({ tipo: "freelancer", id: Number(f.id), nome: String(f.nome ?? "") }));

    // Os freelancers vão para o FIM, num bloco só. Misturados na ordem
    // alfabética, ninguém saberia quem é do quadro e quem é de fora.
    return [
      ...ordenarPorNome(equipe, (l) => l.nome),
      ...ordenarPorNome(extras, (l) => l.nome),
    ];
  }, [refs.colaboradores, freelancers]);

  async function save(
    servico: string,
    cor: string,
    /** null = só o dia clicado (o comportamento de sempre). */
    periodo: { fim: string; incluirFds: boolean } | null,
    /**
     * Observações escritas e ainda NÃO gravadas. É uma LISTA porque na célula
     * nova não existe id para pendurar recado nenhum: o planejador escreve
     * quantos quiser, eles ficam na fila, e todos são gravados de uma vez
     * assim que a alocação passa a existir.
     */
    pendentes: string[] = [],
    /** Quem recebe a MESMA marcação — o card "Participantes" do pop-up. */
    participantes: LinhaAgenda[] = [],
    /** A resposta à pergunta do servidor sobre dias que já têm outro serviço. */
    escolha?: "pular" | "substituir",
  ) {
    if (!cell) return;
    const recados = pendentes.map((t) => t.trim()).filter((t) => t !== "");
    // De quem é a célula. O servidor recusa os dois campos juntos, então aqui
    // sai exatamente um.
    const dono = comoDono(cell.linha);
    const outros = participantes.map(comoDono);

    if (!periodo) {
      /*
       * OS PARTICIPANTES VÃO PRIMEIRO — e pelo lote, não pelo CRUD.
       *
       * A agenda de cada um pode ter, naquele dia, uma folga, um dia de OS
       * aprovado ou outro serviço: coisas que o planejador não está vendo, porque
       * o pop-up é da célula de outra pessoa. O lote já sabe pular as duas
       * primeiras e PERGUNTAR sobre a terceira (409). Indo antes, a pergunta
       * chega sem nada gravado — nem a célula clicada —, e quem responde
       * "manter" ou "substituir" volta aqui com a escolha.
       *
       * A célula clicada continua no CRUD: ela pode ser um dia de OS sendo
       * editado, que o lote (com razão) não toca.
       */
      let lote: RespostaLote | null = null;
      if (outros.length > 0) {
        /*
         * Os recados que a célula clicada JÁ TEM vão junto: o "+ Adicionar" de
         * uma célula existente grava na hora, então eles não estão na fila — e
         * "a mesma marcação" para o participante inclui o que a equipe precisa
         * ler naquele dia. Se a leitura falhar, segue só com os novos.
         */
        let jaGravados: string[] = [];
        if (cell.entry) {
          try {
            jaGravados = (await api.agendaObservacoes(Number(cell.entry.id))).map((o) => o.texto);
          } catch {
            /* segue com os recados novos */
          }
        }
        const paraOsOutros = [...new Set([...jaGravados.reverse(), ...recados])];
        lote = await api.agendaPeriodo({
          inicio: cell.date,
          fim: cell.date,
          servico,
          cor,
          // O dia foi escolhido a dedo na grade: sábado e domingo valem.
          incluir_fim_de_semana: true,
          ...(escolha ? { modo: escolha } : {}),
          ...(paraOsOutros.length ? { observacoes: paraOsOutros } : {}),
          participantes: outros,
        });
        if (lote.conflito) throw new ConflitoPeriodo(lote);
      }

      const data = { ...dono, data: cell.date, servico, cor };
      let alvo = cell.entry ? Number(cell.entry.id) : null;
      if (cell.entry) await api.update("agenda", cell.entry.id, data);
      else {
        const criada = await api.create("agenda", data);
        alvo = Number(criada.id);
      }

      /*
       * A ALOCAÇÃO JÁ EXISTE DAQUI PARA BAIXO — e é por isso que a falha do
       * recado não pode mais subir como exceção.
       *
       * Quando subia, o modal ficava aberto dizendo "erro" e a grade atrás
       * continuava com a célula vazia; quem tentasse de novo levava um 500
       * ("já existe um registro para este colaborador nesta data") e só saía
       * do laço recarregando a página — com o dia já ocupado sem ninguém
       * saber. Agora o dia é fechado e recarregado de qualquer jeito, e o que
       * falhou é DITO na barra, com o texto junto para não se perder.
       */
      let recadoFalhou: string | null = null;
      for (const texto of recados) {
        if (alvo == null) break;
        try {
          await api.agendaObservacaoCriar(alvo, texto);
        } catch {
          recadoFalhou = texto;
          break;
        }
      }

      setCell(null);
      obsMudou.current = false;
      const avisos: string[] = [];
      if (lote) {
        avisos.push(resumoParticipantes(lote, false));
        if ((lote.observacoes_criadas ?? 0) < (lote.observacoes_esperadas ?? 0)) {
          avisos.push("⚠️ As observações não chegaram a todos os participantes — abra o dia de cada um e confira.");
        }
      }
      if (recadoFalhou) {
        avisos.push(
          `A alocação foi salva, mas esta observação NÃO foi gravada: "${recadoFalhou}". ` +
            "Abra o dia e escreva de novo.",
        );
      }
      setResultadoLote(avisos.length ? avisos.join(" ") : null);
      // Com participantes, a grade contorna a célula de cada um: é assim que o
      // planejador confere, de relance, que a equipe toda ficou marcada.
      setDestaque(
        lote
          ? new Set([
              chaveCelula(cell.linha, cell.date),
              ...(lote.pessoas ?? []).flatMap((p) => p.dias.map((d) => chaveCelula(p, d))),
            ])
          : new Set(),
      );
      await load();
      onDataChanged();
      return;
    }

    const r = await api.agendaPeriodo({
      ...dono,
      inicio: cell.date,
      fim: periodo.fim,
      servico,
      cor,
      incluir_fim_de_semana: periodo.incluirFds,
      ...(escolha ? { modo: escolha } : {}),
      // No lote os recados são gravados pelo SERVIDOR, em cada dia efetivamente
      // marcado — daqui não dá para saber quais dias sobreviveram às regras de
      // folga e OS aprovada.
      ...(recados.length ? { observacoes: recados } : {}),
      // O mesmo período, o mesmo serviço, para cada participante — numa
      // chamada só, para a pergunta sobre dias ocupados vir de uma vez.
      ...(outros.length ? { participantes: outros } : {}),
    });

    // 409 com a lista: nada foi gravado, o modal pergunta o que fazer.
    if (r.conflito) throw new ConflitoPeriodo(r);

    setCell(null);
    /*
     * A GRADE VAI ATÉ O QUE FOI MARCADO.
     *
     * A tela mostra 7 dias por padrão; um período de duas semanas seria gravado
     * e a pessoa não veria nada mudar. Aqui a janela pula para o início do
     * período e cresce o suficiente para o fim caber.
     */
    const ini = parseYmd(r.inicio);
    const fim = parseYmd(r.fim);
    const corridos = Math.round((fim.getTime() - startOfWeekMonday(ini).getTime()) / 86400000) + 1;
    setStart(startOfWeekMonday(ini));
    setSpan(Math.min(42, Math.max(7, Math.ceil(corridos / 7) * 7)));
    setDestaque(
      new Set(
        r.pessoas
          ? r.pessoas.flatMap((p) => p.dias.map((d) => chaveCelula(p, d)))
          : (r.dias ?? []).map((d) => chaveCelula(cell.linha, d)),
      ),
    );
    setResultadoLote(
      `${r.colaborador ?? cell.linha.nome}: ${r.mensagem ?? ""}` +
        (r.pulados?.length
          ? ` (${r.pulados.map((p) => `${p.data.slice(8)}/${p.data.slice(5, 7)} — ${p.motivo}`).join("; ")})`
          : "") +
        (outros.length ? ` ${resumoParticipantes(r, true)}` : ""),
    );
    /*
     * O período foi marcado, mas o recado pode não ter colado em todos os dias.
     * Comparar com `observacoes_esperadas` (e não só com zero) é o que pega o
     * caso PARCIAL — sem isso, "gravou em 2 dos 5" passava como sucesso e a
     * equipe dos outros 3 dias nunca leria o que foi combinado.
     */
    if (recados.length > 0) {
      const criadas = r.observacoes_criadas ?? 0;
      const esperadas = r.observacoes_esperadas ?? 0;
      if (criadas < esperadas) {
        setResultadoLote(
          (anterior) =>
            `${anterior ?? ""} ⚠️ ${
              criadas === 0
                ? "A observação NÃO foi gravada"
                : `A observação foi gravada só em parte (${criadas} de ${esperadas})`
            } — abra os dias e confira.`,
        );
      }
    }
    await load();
    onDataChanged();
  }

  async function removeEntry() {
    if (!cell?.entry) return;
    const e = cell.entry;

    /**
     * Confirmação que DIZ o que vai sair.
     *
     * A tabela da agenda é compartilhada: o mesmo dia pode ser uma FOLGA que o
     * colaborador pediu pelo aplicativo (cor amarela) ou um dia de OS que a
     * Gestão da Operação aprovou (`atividade_id`). Apagar aqui apaga para
     * todos — e, agora que isso deixou de ser exclusivo de administrador, a
     * tela precisa avisar antes em vez de sumir com o registro em silêncio.
     */
    const oQue =
      e.cor === "amarelo"
        ? "uma FOLGA do colaborador"
        : e.atividade_id
          ? "um dia de OS aprovado pela Gestão da Operação"
          : "esta marcação";
    const quando = new Date(`${String(e.data).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR");
    if (
      !confirm(
        `Apagar ${oQue} de ${cell.linha.nome} em ${quando}` +
          `${e.servico ? ` (${e.servico})` : ""}?

` +
          "Ela sai também do Planejamento, da Gestão da Operação e do aplicativo do colaborador.",
      )
    ) {
      return;
    }

    await api.remove("agenda", e.id);
    setCell(null);
    await load();
    onDataChanged();
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  const periodo = `${fmtRange.format(days[0])} a ${fmtRange.format(days[days.length - 1])}`;

  return (
    <div className="agenda">
      <div className="agenda-banner">CRONOGRAMA DE SERVIÇOS INTERNOS E EXTERNOS</div>

      <div className="agenda-toolbar">
        <div className="agenda-nav">
          <button className="btn-secondary" onClick={() => setStart((s) => addDays(s, -span))}>
            ‹ Anterior
          </button>
          <button className="btn-secondary" onClick={() => setStart(startOfWeekMonday(new Date()))}>
            Hoje
          </button>
          <button className="btn-secondary" onClick={() => setStart((s) => addDays(s, span))}>
            Próximo ›
          </button>
          <span className="agenda-periodo">{periodo}</span>
        </div>
        {/*
          FREELANCER — o botão mora AQUI, na barra da agenda, e em nenhum outro
          lugar do sistema. É de propósito: quem é contratado por fora entra na
          escala sem virar ficha de RH, sem login e sem aparecer na aba Equipe.
        */}
        <button
          type="button"
          // `modo-leitura-esconde`: some para quem é somente visualização (o
          // "+ " do rótulo escapa do filtro por texto de src/modo-leitura.ts).
          className="btn-secondary modo-leitura-esconde"
          onClick={() => setGerindoFreelancers(true)}
          title="Pôr na escala alguém de fora do quadro (só aparece na agenda)"
        >
          + Freelancer
          {freelancers.filter((f) => f.ativo !== false).length > 0 && (
            <span className="agenda-obs-contador" style={{ marginLeft: 6 }}>
              {freelancers.filter((f) => f.ativo !== false).length}
            </span>
          )}
        </button>
        <div className="agenda-span">
          <label>
            Período:&nbsp;
            <select value={span} onChange={(e) => setSpan(Number(e.target.value))}>
              <option value={7}>1 semana</option>
              <option value={11}>11 dias</option>
              <option value={14}>2 semanas</option>
              {/* Janelas maiores existem por causa do período: marcar 3 semanas
                  e só conseguir ver 2 esconderia parte do que foi feito. */}
              <option value={21}>3 semanas</option>
              <option value={28}>4 semanas</option>
              <option value={42}>6 semanas</option>
            </select>
          </label>
        </div>
      </div>

      {resultadoLote && (
        <div className="modal-notice agenda-resultado">
          <span className="modal-notice-ic">✅</span>
          <span>{resultadoLote}</span>
          <button className="agenda-link" onClick={() => { setResultadoLote(null); setDestaque(new Set()); }}>
            fechar
          </button>
        </div>
      )}

      {/*
        A LEGENDA DE CORES SAIU DAQUI.
        Ela existia para traduzir cada cor num significado fixo ("Treinamento",
        "Externo (rosa)"), mas o significado de cada marcação é digitado no
        próprio serviço da célula — a legenda dizia uma coisa e a célula, outra.
        Sem ela a grade ainda ganha a largura de uma linha inteira, que é o que
        falta numa tela cheia de dias.
      */}

      <div className="agenda-grid-wrap">
        {loading ? (
          <div className="empty">Carregando agenda...</div>
        ) : linhas.length === 0 ? (
          <div className="empty">
            Cadastre colaboradores na aba Equipe — ou use “+ Freelancer” aqui em cima para pôr
            alguém de fora na escala — para montar a agenda.
          </div>
        ) : (
          <table className="agenda-grid">
            <thead>
              <tr>
                <th className="ag-corner">COLABORADORES</th>
                {days.map((d) => {
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th
                      key={ymd(d)}
                      className={`ag-head${weekend ? " ag-weekend" : ""}${ymd(d) === today ? " ag-today" : ""}`}
                    >
                      <span className="ag-head-dow">{fmtWeekday.format(d)}</span>
                      <span className="ag-head-date">{fmtDay.format(d)}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => (
                <tr key={`${linha.tipo}-${linha.id}`}>
                  <th className={`ag-name${linha.tipo === "freelancer" ? " ag-name-freela" : ""}`}>
                    {linha.nome}
                    {/* O selo diz de onde a pessoa veio. Sem ele, uma linha a
                        mais na grade parece contratação nova — e freelancer não
                        aparece no RH, no app do colaborador nem nos indicadores. */}
                    {linha.tipo === "freelancer" && (
                      <span className="ag-freela-tag" title="Freelancer — existe só na agenda">
                        FREELA
                      </span>
                    )}
                  </th>
                  {days.map((d) => {
                    const key = chaveCelula(linha, ymd(d));
                    const entry = map.get(key) ?? null;
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <td
                        key={key}
                        className={`ag-cell${weekend ? " ag-weekend" : ""}${
                          destaque.has(key) ? " ag-novo" : ""
                        }`}
                        onClick={() => {
                          setDestaque(new Set());
                          setResultadoLote(null);
                          setCell({ linha, date: ymd(d), entry });
                        }}
                      >
                        {entry ? (
                          // `pintar` resolve os dois casos: cor da paleta vira
                          // classe, cor escolhida na paleta livre vira estilo.
                          (() => {
                            const p = pintar(entry.cor);
                            const obs = Number(entry.observacoes ?? 0);
                            return (
                              <span className={`ag-block ${p.className}`} style={p.style}>
                                {entry.servico}
                                {/* O NÚMERO VERMELHO, igual ao do ícone de
                                    aplicativo: a célula tem recado. É o único
                                    aviso que sobrevive numa grade cheia de
                                    quadradinhos coloridos vista de longe. */}
                                {obs > 0 && (
                                  <span
                                    className="ag-obs-badge"
                                    title={obs === 1 ? "1 observação" : `${obs} observações`}
                                    aria-label={obs === 1 ? "1 observação" : `${obs} observações`}
                                  >
                                    {obs}
                                  </span>
                                )}
                              </span>
                            );
                          })()
                        ) : (
                          <span className="ag-add modo-leitura-esconde">+</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {cell && (
        <AgendaCellForm
          colabNome={cell.linha.nome}
          ehFreelancer={cell.linha.tipo === "freelancer"}
          date={cell.date}
          dateLabel={fmtDay.format(new Date(cell.date + "T00:00:00"))}
          entry={cell.entry}
          // Os participantes possíveis são as OUTRAS linhas da grade — a equipe
          // do quadro e os freelancers ativos, na mesma ordem em que aparecem.
          candidatos={linhas.filter(
            (l) => !(l.tipo === cell.linha.tipo && l.id === cell.linha.id),
          )}
          ocupacaoNoDia={(l) => {
            const e = map.get(chaveCelula(l, cell.date));
            if (!e) return null;
            if (e.cor === "amarelo") {
              // A folga do aplicativo grava o motivo — ou "Folga" quando não há
              // motivo; sem este cuidado a lista mostraria "Folga · Folga".
              const s = String(e.servico ?? "").trim();
              return s === "" || /^folga\b/i.test(s) ? s || "Folga" : `Folga · ${s}`;
            }
            return e.servico ? String(e.servico) : "ocupado";
          }}
          onCancel={fecharCelula}
          onSave={save}
          onDelete={removeEntry}
          onObservacoesMudaram={() => {
            obsMudou.current = true;
          }}
        />
      )}

      {gerindoFreelancers && (
        <FreelancersModal
          lista={freelancers}
          onClose={() => setGerindoFreelancers(false)}
          onMudou={load}
        />
      )}
    </div>
  );
}

/* ----------------------------- Modal da célula ----------------------------- */
function AgendaCellForm({
  colabNome,
  ehFreelancer,
  date,
  dateLabel,
  entry,
  candidatos,
  ocupacaoNoDia,
  onCancel,
  onSave,
  onDelete,
  onObservacoesMudaram,
}: {
  colabNome: string;
  /** Muda só o rótulo do cabeçalho — a marcação em si é idêntica. */
  ehFreelancer: boolean;
  /** A data clicada, crua (YYYY-MM-DD) — é o início do período. */
  date: string;
  dateLabel: string;
  entry: Row | null;
  /** Quem pode ser participante: as outras linhas da grade. */
  candidatos: LinhaAgenda[];
  /** O que a pessoa já tem no dia clicado (null = livre). */
  ocupacaoNoDia: (l: LinhaAgenda) => string | null;
  onCancel: () => void;
  onSave: (
    servico: string,
    cor: string,
    periodo: { fim: string; incluirFds: boolean } | null,
    pendentes?: string[],
    participantes?: LinhaAgenda[],
    escolha?: "pular" | "substituir",
  ) => Promise<void>;
  onDelete: () => Promise<void>;
  /** Avisa a grade que o número vermelho da célula mudou. */
  onObservacoesMudaram: () => void;
}) {
  // Apagar a alocação NÃO exige mais ser administrador: quem monta o
  // cronograma já cria e edita a célula livremente, e deixar só o desmarcar
  // trancado fazia o planejador "apagar" reescrevendo o serviço — pior, porque
  // a linha continuava ocupando o dia da pessoa. O servidor acompanha
  // (server/index.ts) e registra quem apagou o quê.
  const [servico, setServico] = useState<string>(entry?.servico ?? "");
  const [cor, setCor] = useState<string>(entry?.cor ?? "lavanda");
  /**
   * A cor atual é uma cor LIVRE (hexadecimal) e não uma da paleta?
   *
   * É o que faz o último quadradinho — o da paleta do sistema — aparecer já
   * pintado com a cor escolhida quando a marcação é reaberta para edição. Sem
   * isso, quem gravou um roxo personalizado reabriria a célula e veria nenhum
   * quadradinho marcado, como se a cor tivesse se perdido.
   */
  const corEscolhidaLivre = corLivre(cor);

  /*
   * COR AUTOMÁTICA PELO TEXTO (FALTA, FOLGA, SERCAMP — ver agenda-cores.ts).
   * Troca a cor só quando a PALAVRA muda: reabrir um "FOLGA" e completar para
   * "FOLGA BH" não mexe numa cor que alguém escolheu. Se a pessoa clicar numa
   * cor, a escolha dela vale até fechar o pop-up. E a folga pedida pelo
   * aplicativo (amarela) nunca troca sozinha: a fila "Avaliar Folga" do gestor
   * acha esses dias pela cor, e a folga sumiria de lá sem ninguém ver.
   */
  const corManual = useRef(false);
  const corAntesDoTexto = useRef(entry?.cor ?? "lavanda");
  const folgaDoAplicativo = entry?.cor === "amarelo";
  const corAutomatica =
    !corManual.current && !folgaDoAplicativo && corPeloTexto(servico) === cor;

  function mudarServico(novo: string) {
    if (!corManual.current && !folgaDoAplicativo && palavraDaCor(novo) !== palavraDaCor(servico)) {
      setCor(corPeloTexto(novo) ?? corAntesDoTexto.current);
    }
    setServico(novo);
  }

  function escolherCor(nova: string) {
    corManual.current = true;
    setCor(nova);
  }

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* --------- PERÍODO: "de tal data até tal data, o serviço X" --------- */
  const [modo, setModo] = useState<"dia" | "periodo">("dia");
  const [fim, setFim] = useState<string>(date);
  const [incluirFds, setIncluirFds] = useState(false);
  /** A lista que o servidor devolveu quando há dias de outra pessoa no caminho. */
  const [conflito, setConflito] = useState<any | null>(null);
  /*
   * A PERGUNTA PRECISA SER VISTA. Com o card de participantes aberto, o pop-up
   * fica mais alto que a tela, e a lista de dias ocupados nasce lá embaixo —
   * sem rolar até ela, o Salvar só "fica cinza" e ninguém entende por quê.
   */
  const conflitoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (conflito) conflitoRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [conflito]);

  /**
   * O texto que ainda está sendo escrito, e a FILA do que já foi escrito mas
   * ainda não existe no banco.
   *
   * Ficam AQUI e não dentro da caixa de observações porque quem grava é o
   * "Salvar" do modal: numa célula NOVA não há id para pendurar recado nenhum,
   * então o planejador escreve vários, eles esperam na fila, e todos são
   * gravados de uma vez assim que a alocação passa a existir.
   */
  const [rascunhoObs, setRascunhoObs] = useState("");
  const [pendentesObs, setPendentesObs] = useState<string[]>([]);

  /** Quem recebe a MESMA marcação, além da dona da célula. */
  const [participantes, setParticipantes] = useState<LinhaAgenda[]>([]);

  /*
   * A PRÉVIA, antes de gravar: quantos dias entram e quantos fins de semana
   * saem. É calculada aqui só para a pessoa ver o tamanho do que está pedindo;
   * quem decide de verdade é o servidor, que também conhece folgas e OS.
   */
  const previa = useMemo(() => {
    if (modo === "dia" || fim < date) return null;
    let dias = 0;
    let fds = 0;
    const ate = parseYmd(fim);
    for (let d = parseYmd(date); d <= ate; d.setDate(d.getDate() + 1)) {
      const ehFds = d.getDay() === 0 || d.getDay() === 6;
      if (ehFds && !incluirFds) fds++;
      else dias++;
    }
    return { dias, fds };
  }, [modo, date, fim, incluirFds]);

  const periodoInvalido = modo === "periodo" && fim < date;
  /*
   * Folga é de UMA pessoa: nem em período, nem repetida para participantes.
   * Ela nasce no aplicativo do colaborador e entra na fila do gestor — copiar
   * para a equipe criaria pedidos que ninguém fez.
   */
  const emLote = modo === "periodo" || participantes.length > 0;
  const folgaNoLote = emLote && cor === "amarelo";

  async function enviar(escolha?: "pular" | "substituir") {
    setSaving(true);
    setError(null);
    try {
      await onSave(
        servico.trim(),
        cor,
        modo === "periodo" ? { fim, incluirFds } : null,
        // A fila MAIS o que ficou no campo: quem digita e clica direto em
        // Salvar espera que o texto vá junto, não que suma.
        [...pendentesObs, rascunhoObs],
        participantes,
        escolha,
      );
    } catch (err) {
      // Conflito não é erro: é a pergunta do servidor sobre os dias ocupados.
      if (err instanceof ConflitoPeriodo) setConflito(err.dados);
      else setError(err instanceof Error ? err.message : "Erro ao salvar");
      setSaving(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await enviar();
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card agenda-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{entry ? "Editar alocação" : "Nova alocação"}</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        <form className="modal-body" onSubmit={submit}>
          <div className="agenda-modal-info">
            <strong>
              {colabNome}
              {ehFreelancer && <span className="ag-freela-tag">FREELA</span>}
            </strong>
            <span className="muted">{dateLabel}</span>
          </div>

          {/* QUEM VAI JUNTO — a mesma marcação na agenda de cada um. Ver
              ParticipantesBox abaixo. */}
          <ParticipantesBox
            candidatos={candidatos}
            selecionados={participantes}
            onChange={(lista) => {
              setParticipantes(lista);
              setConflito(null);
            }}
            ocupacaoNoDia={ocupacaoNoDia}
            bloqueado={cor === "amarelo"}
          />

          {/* DE TAL DATA ATÉ TAL DATA — o dia clicado é sempre o início. */}
          <div className="agenda-periodo-box">
            <label className="agenda-radio">
              <input
                type="radio"
                checked={modo === "dia"}
                onChange={() => { setModo("dia"); setConflito(null); }}
              />
              <span>Só este dia</span>
            </label>
            <label className="agenda-radio">
              <input
                type="radio"
                checked={modo === "periodo"}
                onChange={() => { setModo("periodo"); setConflito(null); }}
              />
              <span>Até</span>
            </label>
            <input
              type="date"
              className="agenda-data-fim"
              value={fim}
              min={date}
              disabled={modo !== "periodo"}
              onChange={(e) => { setFim(e.target.value); setConflito(null); }}
            />
            {modo === "periodo" && (
              <label className="agenda-check">
                <input
                  type="checkbox"
                  checked={incluirFds}
                  onChange={(e) => { setIncluirFds(e.target.checked); setConflito(null); }}
                />
                <span>incluir sábado e domingo</span>
              </label>
            )}
          </div>

          {previa && (
            <p className="agenda-previa">
              {periodoInvalido
                ? "A data final é antes da inicial."
                : `${previa.dias} dia(s) serão marcados` +
                  (previa.fds > 0 ? ` · ${previa.fds} de fim de semana pulados` : "")}
            </p>
          )}

          <label className="field">
            <span>Serviço / Local</span>
            <input
              autoFocus
              value={servico}
              placeholder="Ex.: NUTREN – RIO VERDE GO"
              onChange={(e) => mudarServico(e.target.value)}
            />
          </label>

          <label className="field" style={{ marginTop: 14 }}>
            <span>
              Cor
              {corAutomatica && <span className="muted"> · automática para {palavraDaCor(servico)}</span>}
            </span>
          </label>
          <div className="agenda-cor-grid">
            {AGENDA_CORES.map((c) => {
              /*
               * FOLGA NÃO SE MARCA EM LOTE: ela é pedida pelo colaborador no
               * aplicativo e avaliada pela Gestão da Operação. Um período
               * amarelo — ou uma folga copiada para os participantes — criaria
               * pedidos que ninguém fez na fila do gestor.
               */
              const bloqueada = emLote && c.key === "amarelo";
              return (
                <button
                  type="button"
                  key={c.key}
                  className={`agenda-cor-opt ag-${c.key}${cor === c.key ? " selected" : ""}${
                    bloqueada ? " agenda-cor-bloqueada" : ""
                  }`}
                  title={
                    bloqueada
                      ? "Folga é pedida pelo colaborador no aplicativo — não se marca em período nem para participantes"
                      : c.nome
                  }
                  // O quadradinho não tem texto: o nome da cor vive aqui, para
                  // leitor de tela e para quem navega pelo teclado.
                  aria-label={c.nome}
                  aria-pressed={cor === c.key}
                  disabled={bloqueada}
                  onClick={() => escolherCor(c.key)}
                >
                  {/* Só o "escolhida" aparece escrito — e como sinal, não como
                      rótulo: sem ele, no meio de treze quadrados coloridos, a
                      borda sozinha some. */}
                  {cor === c.key && <span aria-hidden="true">✓</span>}
                </button>
              );
            })}

            {/*
              COR LIVRE — a paleta do próprio sistema operacional.
              É um <input type="color"> escondido dentro do quadradinho: clicar
              abre o seletor de cores do Windows, e a cor escolhida é gravada
              como hexadecimal ("#7c3aed") no mesmo campo das outras. Assim a
              pessoa não fica presa às cores que alguém escolheu por ela.
              A borda tracejada e o "+" dizem que este quadrado é diferente
              dos outros: ele não É uma cor, ele ABRE a escolha de uma.
            */}
            <label
              className={`agenda-cor-opt agenda-cor-livre${corEscolhidaLivre ? " selected" : ""}`}
              style={corEscolhidaLivre ? { background: corEscolhidaLivre } : undefined}
              title={
                corEscolhidaLivre
                  ? `Cor personalizada ${corEscolhidaLivre} — clique para trocar`
                  : "Escolher outra cor na paleta"
              }
            >
              <input
                type="color"
                value={corEscolhidaLivre ?? COR_LIVRE_PADRAO}
                aria-label="Escolher outra cor na paleta"
                onChange={(e) => escolherCor(e.target.value.toLowerCase())}
              />
              <span aria-hidden="true">{corEscolhidaLivre ? "✓" : "+"}</span>
            </label>
          </div>

          {/* OBSERVAÇÕES — o que o planejador combina para aquele dia e que a
              equipe precisa ler antes de sair. Ver ObservacoesBox abaixo. */}
          <ObservacoesBox
            agendaId={entry ? Number(entry.id) : null}
            rascunho={rascunhoObs}
            setRascunho={setRascunhoObs}
            pendentes={pendentesObs}
            setPendentes={setPendentesObs}
            emPeriodo={modo === "periodo"}
            comParticipantes={participantes.length > 0}
            onMudou={onObservacoesMudaram}
          />

          {folgaNoLote && (
            <div className="form-error">
              Folga não se marca em período nem para participantes: ela é pedida pelo colaborador no
              aplicativo e avaliada pela Gestão da Operação. Escolha outra cor
              {participantes.length > 0 ? " ou tire os participantes" : ""}.
            </div>
          )}

          {/*
            O QUE ESTÁ NO CAMINHO — a conversa antes de passar por cima.
            Num dia só, a pessoa está olhando a célula; num período de vinte
            dias, ela não está olhando célula nenhuma.
          */}
          {conflito && (
            <div className="agenda-conflito" ref={conflitoRef}>
              {conflito.bloqueados?.length > 0 && (
                <>
                  <strong>Estes dias não serão marcados:</strong>
                  <ul>
                    {conflito.bloqueados.map((b: any) => (
                      <li key={`${b.pessoa ?? ""}|${b.data}`}>
                        {participantes.length > 0 && b.pessoa ? <strong className="agenda-conflito-quem">{b.pessoa}</strong> : null}
                        {b.data.slice(8)}/{b.data.slice(5, 7)} — {b.motivo}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {conflito.substituiveis?.length > 0 && (
                <>
                  <strong>
                    {conflito.substituiveis.length} dia(s) já têm outro serviço marcado:
                  </strong>
                  <ul>
                    {conflito.substituiveis.map((c: any) => (
                      // Com participantes, o mesmo dia aparece para várias
                      // pessoas — o nome na frente é o que diz de quem é.
                      <li key={`${c.pessoa ?? ""}|${c.data}`}>
                        {participantes.length > 0 && c.pessoa ? <strong className="agenda-conflito-quem">{c.pessoa}</strong> : null}
                        {c.data.slice(8)}/{c.data.slice(5, 7)} — “{c.servico || c.cor}”
                      </li>
                    ))}
                  </ul>
                  <div className="agenda-conflito-acoes">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={saving}
                      onClick={() => void enviar("pular")}
                    >
                      Manter esses dias
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-danger-text"
                      disabled={saving}
                      onClick={() => void enviar("substituir")}
                    >
                      Substituir pelo novo serviço
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="modal-foot">
            {entry && (
              <button
                type="button"
                className="btn-secondary btn-danger-text"
                onClick={onDelete}
                style={{ marginRight: "auto" }}
              >
                <IconTrash size={16} /> Excluir
              </button>
            )}
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || periodoInvalido || folgaNoLote || !!conflito}
            >
              {saving
                ? "Salvando..."
                : (modo === "periodo" && previa && previa.dias > 1
                    ? `Marcar ${previa.dias} dias`
                    : "Salvar") +
                  // O botão diz o tamanho do que vai acontecer: com participantes,
                  // a marcação sai para mais de uma agenda.
                  (participantes.length > 0 ? ` para ${participantes.length + 1} pessoas` : "")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


/* --------------------------- Participantes do serviço --------------------------- */
/** Busca sem acento e sem maiúscula: "joao" acha "João". */
const semAcento = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/**
 * QUEM VAI JUNTO NESTE SERVIÇO.
 *
 * Um serviço de campo quase nunca é de uma pessoa só, e marcar a equipe era
 * abrir o mesmo pop-up três vezes, digitar o mesmo serviço três vezes e torcer
 * para não errar uma letra. Aqui o planejador escolhe os outros, e o Salvar
 * grava a MESMA marcação (serviço, cor, período e observações) na agenda de
 * cada um.
 *
 * Os candidatos são as outras linhas da grade — exatamente quem aparece na
 * agenda —, cada um com o que já tem no dia clicado ao lado do nome. É o que
 * evita escolher às cegas alguém que está de folga ou em outra obra; e se
 * mesmo assim houver serviço no caminho, o servidor pergunta antes de gravar.
 *
 * DECLARADO NO TOPO DO MÓDULO, e não dentro do AgendaCellForm, pelo mesmo
 * motivo da ObservacoesBox: componente declarado dentro de outro é remontado a
 * cada tecla, e o campo de busca perderia o foco no meio do nome.
 */
function ParticipantesBox({
  candidatos,
  selecionados,
  onChange,
  ocupacaoNoDia,
  bloqueado,
}: {
  candidatos: LinhaAgenda[];
  selecionados: LinhaAgenda[];
  onChange: (lista: LinhaAgenda[]) => void;
  ocupacaoNoDia: (l: LinhaAgenda) => string | null;
  /** Folga na célula: é de uma pessoa só, não se repete para ninguém. */
  bloqueado: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");

  const chave = (l: LinhaAgenda) => `${l.tipo}-${l.id}`;
  const escolhidos = new Set(selecionados.map(chave));

  const filtrados = useMemo(() => {
    const q = semAcento(busca);
    return q === "" ? candidatos : candidatos.filter((l) => semAcento(l.nome).includes(q));
  }, [candidatos, busca]);

  function alternar(l: LinhaAgenda) {
    onChange(
      escolhidos.has(chave(l))
        ? selecionados.filter((s) => chave(s) !== chave(l))
        : // Na ordem da grade, não na ordem dos cliques: a lista de nomes do
          // card fica igual à de cima para baixo da agenda.
          candidatos.filter((c) => escolhidos.has(chave(c)) || chave(c) === chave(l)),
    );
  }

  return (
    <div className="agenda-part modo-leitura-esconde">
      <div className="agenda-part-head">
        <span className="agenda-part-titulo">
          <IconUsers size={14} /> Participantes
          {selecionados.length > 0 && (
            <span className="agenda-part-contador">{selecionados.length}</span>
          )}
        </span>
        {!bloqueado && candidatos.length > 0 && (
          <button
            type="button"
            className="agenda-link agenda-part-abrir"
            onClick={() => {
              setAberto((a) => !a);
              setBusca("");
            }}
          >
            {aberto ? "Pronto" : "+ Adicionar participantes"}
          </button>
        )}
      </div>

      {selecionados.length > 0 && (
        <div className="agenda-part-chips">
          {selecionados.map((l) => (
            <span className="agenda-part-chip" key={chave(l)}>
              {l.nome}
              {l.tipo === "freelancer" && <span className="ag-freela-tag">FREELA</span>}
              <button
                type="button"
                aria-label={`Tirar ${l.nome} dos participantes`}
                title="Tirar dos participantes"
                onClick={() => alternar(l)}
              >
                <IconClose size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {bloqueado ? (
        <p className="agenda-part-dica muted">
          Folga é de uma pessoa só — não se repete para participantes.
        </p>
      ) : selecionados.length > 0 ? (
        <p className="agenda-part-dica muted">
          A mesma marcação vai para a agenda de cada um ao salvar.
        </p>
      ) : !aberto ? (
        <p className="agenda-part-dica muted">
          Vai mais alguém neste serviço? Adicione e a agenda deles é marcada junto.
        </p>
      ) : null}

      {aberto && !bloqueado && (
        <div className="agenda-part-picker">
          <input
            className="agenda-part-busca"
            autoFocus
            value={busca}
            placeholder="Buscar pelo nome..."
            onChange={(e) => setBusca(e.target.value)}
            // Enter aqui não pode SALVAR o pop-up no meio da escolha.
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          />
          <div className="agenda-part-lista">
            {filtrados.length === 0 ? (
              <p className="muted agenda-part-vazio">Ninguém com esse nome na agenda.</p>
            ) : (
              filtrados.map((l) => {
                const ocupado = ocupacaoNoDia(l);
                return (
                  <label className="agenda-part-item" key={chave(l)}>
                    <input
                      type="checkbox"
                      checked={escolhidos.has(chave(l))}
                      onChange={() => alternar(l)}
                    />
                    <span className="agenda-part-nome">
                      {l.nome}
                      {l.tipo === "freelancer" && <span className="ag-freela-tag">FREELA</span>}
                    </span>
                    {/* O que a pessoa já tem no dia clicado — para não escalar
                        às cegas quem está de folga ou em outra obra. */}
                    <span className="agenda-part-ocupacao muted" title={ocupado ?? "Livre neste dia"}>
                      {ocupado ?? "livre"}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------- Observações da célula --------------------------- */
/**
 * Data/hora curta do recado ("03/09/26 14:20"). Sem os segundos e sem o ano de
 * quatro dígitos: numa lista de recados o que importa é "quando, mais ou
 * menos", e a linha cabe no celular.
 */
const fmtCarimbo = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * OS RECADOS DAQUELE DIA — e são VÁRIOS, em qualquer situação.
 *
 * Onde eles ficam até virarem linha no banco é que muda:
 *
 *  • CÉLULA QUE JÁ EXISTE: a lista é ao vivo. "+ Adicionar" grava na hora e o
 *    recado aparece com autor e data, porque isto é uma conversa (vários
 *    recados, escritos em momentos diferentes) e não um campo de formulário.
 *  • CÉLULA NOVA: ainda não existe id para pendurar recado nenhum. Então
 *    "+ Adicionar" põe o texto numa FILA aqui na tela, com o mesmo desenho da
 *    lista, e o Salvar grava todos de uma vez. Sem a fila, o modal chamado
 *    "Nova alocação" só aceitaria UM recado — que era justamente o contrário
 *    do que foi pedido.
 *  • PERÍODO: o "+ Adicionar" some. Ali o recado é dos quinze dias e quem
 *    grava é o servidor, em cada dia que sobreviveu às regras de folga e OS.
 *
 * DECLARADO NO TOPO DO MÓDULO, e não dentro do AgendaCellForm: componente
 * declarado dentro de outro é REMONTADO a cada tecla, o que faria o campo
 * perder o foco no meio da frase (o mesmo tropeço já corrigido na
 * AvaliacaoPublica do RH).
 */
function ObservacoesBox({
  agendaId,
  rascunho,
  setRascunho,
  pendentes,
  setPendentes,
  emPeriodo,
  comParticipantes,
  onMudou,
}: {
  /** null = a alocação ainda não existe; os recados vão junto do Salvar. */
  agendaId: number | null;
  rascunho: string;
  setRascunho: (v: string) => void;
  /** Recados escritos mas ainda não gravados (célula nova). */
  pendentes: string[];
  setPendentes: (v: string[]) => void;
  emPeriodo: boolean;
  /** Há participantes: o recado vai junto para a agenda de cada um. */
  comParticipantes: boolean;
  onMudou: () => void;
}) {
  const [lista, setLista] = useState<Observacao[]>([]);
  const [carregando, setCarregando] = useState(agendaId != null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    if (agendaId == null) {
      setLista([]);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    try {
      setLista(await api.agendaObservacoes(agendaId));
      setErro(null);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível carregar as observações.");
    } finally {
      setCarregando(false);
    }
  }, [agendaId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function adicionar() {
    const texto = rascunho.trim();
    if (texto === "" || ocupado) return;

    // CÉLULA NOVA: ainda não há id, então o recado entra na fila da tela e é
    // gravado pelo Salvar junto com a alocação.
    if (agendaId == null) {
      setPendentes([...pendentes, texto]);
      setRascunho("");
      return;
    }

    setOcupado(true);
    setErro(null);
    try {
      const nova = await api.agendaObservacaoCriar(agendaId, texto);
      setLista((l) => [nova, ...l]);
      setRascunho("");
      onMudou();
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível salvar a observação.");
    } finally {
      setOcupado(false);
    }
  }

  async function remover(obs: Observacao) {
    if (agendaId == null || ocupado) return;
    if (!confirm(`Apagar esta observação?

“${obs.texto}”`)) return;
    setOcupado(true);
    setErro(null);
    try {
      await api.agendaObservacaoRemover(agendaId, obs.id);
      setLista((l) => l.filter((o) => o.id !== obs.id));
      onMudou();
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível apagar a observação.");
    } finally {
      setOcupado(false);
    }
  }

  const novaCelula = agendaId == null;
  /**
   * O "+ Adicionar" some SÓ no período. Ali o recado é para os quinze dias e
   * quem repete em cada um é o servidor; um botão que pendurasse o texto no
   * primeiro dia seria uma armadilha silenciosa. Na célula nova ele fica, e
   * enfileira — é o que permite escrever mais de um recado antes de salvar.
   */
  const podeAdicionar = !emPeriodo;

  return (
    <div className="agenda-obs">
      <div className="agenda-obs-head">
        <span>Observações</span>
        {lista.length + pendentes.length > 0 && (
          <span className="agenda-obs-contador">{lista.length + pendentes.length}</span>
        )}
      </div>

      {!novaCelula && (
        <div className="agenda-obs-lista">
          {carregando ? (
            <p className="muted">Carregando observações...</p>
          ) : erro ? (
            /* ERRO NÃO É LISTA VAZIA. Antes, um GET que falhasse caía no
               "Nenhuma observação neste dia" — uma afirmação de fato — e o
               planejador reescrevia recados que já existiam (com a grade
               mostrando "3" na bolinha ao lado). */
            <div className="form-error">
              {erro}{" "}
              <button type="button" className="agenda-link" onClick={() => void carregar()}>
                tentar de novo
              </button>
            </div>
          ) : lista.length === 0 ? (
            <p className="muted">Nenhuma observação neste dia.</p>
          ) : (
            lista.map((o) => (
              <div className="agenda-obs-item" key={o.id}>
                <p className="agenda-obs-texto">{o.texto}</p>
                <div className="agenda-obs-rodape">
                  <span className="muted">
                    {o.autor ? `${o.autor} · ` : ""}
                    {fmtCarimbo.format(new Date(o.created_at))}
                  </span>
                  <button
                    type="button"
                    className="agenda-link"
                    disabled={ocupado}
                    onClick={() => void remover(o)}
                  >
                    <IconTrash size={13} /> apagar
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* A FILA da célula nova: o que já foi escrito e vai junto no Salvar.
          Mesmo desenho da lista gravada, com um rótulo honesto. */}
      {pendentes.length > 0 && (
        <div className="agenda-obs-lista">
          {pendentes.map((texto, i) => (
            <div className="agenda-obs-item" key={`${i}-${texto.slice(0, 12)}`}>
              <p className="agenda-obs-texto">{texto}</p>
              <div className="agenda-obs-rodape">
                <span className="muted">será gravada ao salvar</span>
                <button
                  type="button"
                  className="agenda-link"
                  onClick={() => setPendentes(pendentes.filter((_, j) => j !== i))}
                >
                  <IconTrash size={13} /> remover
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Escrever recado some para quem é somente visualização (a lista acima
          continua): ver `modo-leitura-esconde` em styles.css. */}
      <textarea
        className="agenda-obs-campo modo-leitura-esconde"
        rows={2}
        maxLength={2000}
        value={rascunho}
        placeholder="Ex.: levar o alicate amperímetro — o cliente só libera a subestação depois das 8h"
        onChange={(e) => setRascunho(e.target.value)}
      />

      <div className="agenda-obs-acoes modo-leitura-esconde">
        <span className="muted">
          {emPeriodo
            ? "Será gravada em cada dia do período, junto com a alocação."
            : novaCelula
              ? "Adicione quantas quiser — todas são gravadas ao salvar a alocação."
              : "O colaborador vê o recado na agenda dele, no aplicativo."}
          {comParticipantes && " Vai também para os participantes."}
        </span>
        {podeAdicionar && (
          <button
            type="button"
            className="btn-secondary"
            disabled={ocupado || rascunho.trim() === ""}
            onClick={() => void adicionar()}
          >
            + Adicionar
          </button>
        )}
      </div>

      {erro && <div className="form-error">{erro}</div>}
    </div>
  );
}
